import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Brain, CheckCircle2, CircleDashed, FileText, PlayCircle } from "lucide-react";
import { errorMessage, request } from "../lib/api";
import { Status } from "../components/ui";
import { itemLabelKey } from "../lib/pdDayRoutes";
import type { HistoryEntry, TeacherHistory } from "../types";

// Progress is shown per PD Day, in the day's own order. Only whether each
// item is done — no scores, pass/fail or attempts.
function groupByDay(entries: HistoryEntry[]): { title: string; entries: HistoryEntry[] }[] {
  const groups = new Map<string, { title: string; entries: HistoryEntry[] }>();
  for (const entry of entries) {
    const group = groups.get(entry.pd_day_id) ?? { title: entry.pd_day_title, entries: [] };
    group.entries.push(entry);
    groups.set(entry.pd_day_id, group);
  }
  return [...groups.values()];
}

const typeIcon = (type: string) =>
  type === "video" ? PlayCircle : type === "quiz" ? Brain : FileText;

export function History() {
  const { t } = useTranslation();
  const [data, setData] = useState<TeacherHistory | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    request<TeacherHistory>("/api/teacher/history")
      .then(setData)
      .catch((cause) => setError(errorMessage(cause)));
  }, []);

  if (error) return <Status variant="error" title={t("historyUnavailable")} message={error} />;
  if (!data) return <Status variant="loading" title={t("loading")} message="" />;

  return (
    <div className="flex flex-col gap-5 pb-6 pt-2">
      <h1 className="px-1 text-xl font-semibold tracking-tight text-app-ink">{t("progress")}</h1>

      {data.progress.length === 0 && (
        <Status variant="empty" title={t("progress")} message={t("noProgressYet")} />
      )}

      {groupByDay(data.progress).map(({ title, entries }) => {
        const done = entries.filter((entry) => entry.status === "completed").length;
        return (
          <section key={title} className="flex flex-col gap-2">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-app-muted">
                {title}
              </h2>
              <span className="text-xs font-semibold text-app-muted">
                {done}/{entries.length}
              </span>
            </div>
            <ol className="flex flex-col overflow-hidden rounded-xl bg-surface shadow-raised">
              {entries.map((entry) => {
                const completed = entry.status === "completed";
                const Icon = typeIcon(entry.type);
                return (
                  <li
                    key={entry.item_id}
                    className="flex items-center gap-3 border-b border-app-line px-4 py-3.5 last:border-b-0"
                  >
                    <span className="grid size-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
                      <Icon size={18} />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-base font-semibold text-app-ink">
                      {t(itemLabelKey(entry.type), { number: entry.module_number })}
                    </span>
                    <span
                      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${completed ? "bg-success-bg text-success-text" : "bg-app-field text-app-muted"}`}
                    >
                      {completed ? <CheckCircle2 size={14} /> : <CircleDashed size={14} />}
                      {t(completed ? "historyDone" : "historyStarted")}
                    </span>
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })}

      {data.form_submissions.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="px-1 text-[11px] font-semibold uppercase tracking-wider text-app-muted">
            {t("pastAnswers")}
          </h2>
          {data.form_submissions.map((submission) => (
            <article
              key={submission.id}
              className="flex flex-col gap-1.5 rounded-xl bg-surface p-4 shadow-raised"
            >
              <strong className="text-base font-semibold text-app-ink">{submission.title}</strong>
              {submission.responses.map((response) => (
                <p className="text-sm leading-relaxed text-app-copy" key={response.field_id}>
                  <span className="font-semibold text-app-ink">{response.label}:</span>{" "}
                  {String(response.value)}
                </p>
              ))}
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
